import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2,
  Loader2,
  ArrowRight,
  Search,
  Copy,
  Headphones,
  MessageSquareWarning,
  Ticket,
} from "lucide-react";

const STATUS_COLOR = {
  Baru: "bg-[#0A84FF]/15 text-[#0A84FF] border-[#0A84FF]/30",
  Diproses: "bg-[#FF9F0A]/15 text-[#FF9F0A] border-[#FF9F0A]/30",
  Selesai: "bg-[#32D74B]/15 text-[#32D74B] border-[#32D74B]/30",
  Ditolak: "bg-[#FF453A]/15 text-[#FF453A] border-[#FF453A]/30",
};

export default function ServiceComplaintPage() {
  const [tab, setTab] = useState("ajukan"); // ajukan | lacak
  const [cfg, setCfg] = useState({ service_topics: [], complaint_topics: [], prodi_options: [], app_name: "UT Mataram", phone_label: "No. Handphone", phone_enabled: true, phone_required: true });

  // form
  const [type, setType] = useState("layanan");
  const [fillDate, setFillDate] = useState(new Date().toISOString().slice(0, 10));
  const [topic, setTopic] = useState("");
  const [nim, setNim] = useState("");
  const [nama, setNama] = useState("");
  const [prodi, setProdi] = useState("");
  const [phone, setPhone] = useState("");
  const [customVals, setCustomVals] = useState({});
  const [description, setDescription] = useState("");
  const [hp, setHp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {ticket_no, message}
  const [err, setErr] = useState("");

  // tracking
  const [trackNo, setTrackNo] = useState("");
  const [tracking, setTracking] = useState(false);
  const [trackData, setTrackData] = useState(null);
  const [trackErr, setTrackErr] = useState("");

  useEffect(() => {
    document.documentElement.classList.remove("light");
    document.documentElement.classList.add("dark");
    api.get("/public/ticket-config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setTopic("");
  }, [type]);

  const topics = type === "layanan" ? cfg.service_topics : cfg.complaint_topics;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!topic || !nim || !nama || !description) {
      setErr("Topik, NIM, Nama, dan Deskripsi wajib diisi.");
      return;
    }
    if (cfg.phone_enabled && cfg.phone_required && !phone.trim()) {
      setErr(`${cfg.phone_label || "No. Handphone"} wajib diisi.`);
      return;
    }
    for (const f of (cfg.custom_fields || [])) {
      if (f.required && !String(customVals[f.key] || "").trim()) {
        setErr(`${f.label} wajib diisi.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/tickets", {
        type, fill_date: fillDate, topic, nim, nama, prodi, phone, description, custom: customVals, hp,
      });
      setResult(data);
    } catch (ex) {
      setErr(ex.response?.status === 429 ? "Terlalu banyak pengiriman. Coba lagi beberapa menit." : (ex.response?.data?.detail || "Gagal mengirim"));
    } finally {
      setSubmitting(false);
    }
  };

  const track = async (e) => {
    e.preventDefault();
    setTrackErr("");
    setTrackData(null);
    if (!trackNo.trim()) return;
    setTracking(true);
    try {
      const { data } = await api.get(`/public/tickets/${encodeURIComponent(trackNo.trim())}`);
      setTrackData(data);
    } catch (ex) {
      setTrackErr(ex.response?.status === 404 ? "Nomor tiket tidak ditemukan." : "Gagal melacak tiket");
    } finally {
      setTracking(false);
    }
  };

  const resetForm = () => {
    setResult(null);
    setTopic(""); setNim(""); setNama(""); setProdi(""); setDescription(""); setPhone(""); setCustomVals({});
  };

  // ---- Success screen ----
  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#070707] bg-grid-pattern">
        <div className="bg-white/[0.04] border border-white/10 backdrop-blur-xl p-8 sm:p-10 max-w-lg w-full text-center rounded-2xl" data-testid="ticket-success">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#32D74B]/15 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-[#32D74B]" strokeWidth={1.5} />
          </div>
          <h2 className="font-heading text-2xl text-white font-semibold mt-5">Pengajuan Terkirim</h2>
          <p className="text-sm text-white/70 mt-3 leading-relaxed">{result.message}</p>
          <div className="mt-6 p-4 rounded-xl bg-black/40 border border-white/10">
            <div className="text-[10px] tracking-[0.25em] uppercase text-white/40 mb-1">Nomor Tiket Anda</div>
            <div className="flex items-center justify-center gap-2">
              <span data-testid="ticket-number" className="font-mono text-2xl font-bold text-primary">{result.ticket_no}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(result.ticket_no); }}
                className="text-white/50 hover:text-white transition-colors"
                title="Salin"
                data-testid="copy-ticket-number"
              >
                <Copy className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
            <div className="text-xs text-white/40 mt-2">Simpan nomor ini untuk melacak status pengajuan Anda.</div>
          </div>
          <div className="flex gap-2 mt-6">
            <Button onClick={resetForm} variant="outline" className="flex-1 border-white/15 bg-transparent text-white hover:bg-white/10" data-testid="new-ticket-button">
              Ajukan Lagi
            </Button>
            <Button
              onClick={() => { setTrackNo(result.ticket_no); setResult(null); setTab("lacak"); }}
              className="flex-1 bg-primary hover:bg-[#0056B3]"
              data-testid="goto-track-button"
            >
              Lacak Status
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070707] bg-grid-pattern flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-[10px] tracking-[0.22em] uppercase text-white/90 backdrop-blur-sm mb-4">
            <Ticket className="w-3 h-3 text-[#FF9F0A]" strokeWidth={2} />
            {cfg.app_name}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">Layanan & Keluhan</h1>
          <p className="text-sm text-white/50 mt-2">Ajukan permintaan layanan atau sampaikan keluhan Anda, lalu pantau statusnya.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 p-1 rounded-xl bg-white/[0.04] border border-white/10 mb-5">
          <button
            data-testid="tab-ajukan"
            onClick={() => setTab("ajukan")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === "ajukan" ? "bg-primary text-white" : "text-white/60 hover:text-white"}`}
          >
            Ajukan
          </button>
          <button
            data-testid="tab-lacak"
            onClick={() => setTab("lacak")}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${tab === "lacak" ? "bg-primary text-white" : "text-white/60 hover:text-white"}`}
          >
            Lacak Status
          </button>
        </div>

        <div className="bg-[#0C0C0E] border border-white/10 rounded-2xl p-6 sm:p-8">
          {tab === "ajukan" ? (
            <form onSubmit={submit} className="space-y-4" data-testid="ticket-form" noValidate>
              {/* honeypot */}
              <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="ticket-honeypot"
                value={hp} onChange={(e) => setHp(e.target.value)}
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />

              {/* Type selector */}
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-white/60 block mb-1.5">Jenis Pengajuan</label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" data-testid="type-layanan" onClick={() => setType("layanan")}
                    className={`flex items-center justify-center gap-2 py-3 rounded-lg border text-sm font-medium transition-colors ${type === "layanan" ? "border-primary bg-primary/15 text-white" : "border-white/10 text-white/60 hover:border-white/25"}`}>
                    <Headphones className="w-4 h-4" strokeWidth={1.5} /> Layanan
                  </button>
                  <button type="button" data-testid="type-keluhan" onClick={() => setType("keluhan")}
                    className={`flex items-center justify-center gap-2 py-3 rounded-lg border text-sm font-medium transition-colors ${type === "keluhan" ? "border-[#FF9F0A] bg-[#FF9F0A]/15 text-white" : "border-white/10 text-white/60 hover:border-white/25"}`}>
                    <MessageSquareWarning className="w-4 h-4" strokeWidth={1.5} /> Keluhan
                  </button>
                </div>
              </div>

              {/* Tanggal */}
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-white/60 block mb-1.5">Tanggal Pengisian <span className="text-[#FF453A]">*</span></label>
                <Input type="date" value={fillDate} onChange={(e) => setFillDate(e.target.value)} data-testid="ticket-date" className="bg-black/30 border-white/10 text-white" required />
              </div>

              {/* Topik */}
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-white/60 block mb-1.5">
                  Topik {type === "layanan" ? "Layanan" : "Keluhan"} <span className="text-[#FF453A]">*</span>
                </label>
                <Select value={topic} onValueChange={setTopic}>
                  <SelectTrigger data-testid="ticket-topic" className="bg-black/30 border-white/10 text-white">
                    <SelectValue placeholder="Pilih topik..." />
                  </SelectTrigger>
                  <SelectContent>
                    {(topics || []).map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-white/60 block mb-1.5">NIM <span className="text-[#FF453A]">*</span></label>
                  <Input value={nim} onChange={(e) => setNim(e.target.value)} data-testid="ticket-nim" className="bg-black/30 border-white/10 text-white" required />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-white/60 block mb-1.5">Prodi</label>
                  <Select value={prodi} onValueChange={setProdi}>
                    <SelectTrigger data-testid="ticket-prodi" className="bg-black/30 border-white/10 text-white">
                      <SelectValue placeholder="Pilih prodi..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(cfg.prodi_options || []).map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-white/60 block mb-1.5">Nama Lengkap <span className="text-[#FF453A]">*</span></label>
                <Input value={nama} onChange={(e) => setNama(e.target.value)} data-testid="ticket-nama" className="bg-black/30 border-white/10 text-white" required />
              </div>

              {cfg.phone_enabled && (
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-white/60 block mb-1.5">
                    {cfg.phone_label || "No. Handphone"} {cfg.phone_required && <span className="text-[#FF453A]">*</span>}
                  </label>
                  <Input type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="08xxxxxxxxxx"
                    data-testid="ticket-phone" className="bg-black/30 border-white/10 text-white" />
                </div>
              )}

              {(cfg.custom_fields || []).map((f) => (
                <div key={f.key} data-testid={`custom-field-wrap-${f.key}`}>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-white/60 block mb-1.5">
                    {f.label} {f.required && <span className="text-[#FF453A]">*</span>}
                  </label>
                  {f.type === "select" ? (
                    <Select value={customVals[f.key] || ""} onValueChange={(v) => setCustomVals((p) => ({ ...p, [f.key]: v }))}>
                      <SelectTrigger data-testid={`ticket-custom-${f.key}`} className="bg-black/30 border-white/10 text-white">
                        <SelectValue placeholder="Pilih..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(f.options || []).map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                      value={customVals[f.key] || ""}
                      onChange={(e) => setCustomVals((p) => ({ ...p, [f.key]: e.target.value }))}
                      data-testid={`ticket-custom-${f.key}`}
                      className="bg-black/30 border-white/10 text-white"
                    />
                  )}
                </div>
              ))}

              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-white/60 block mb-1.5">Deskripsi Permohonan {type === "layanan" ? "Layanan" : "Keluhan"} <span className="text-[#FF453A]">*</span></label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} data-testid="ticket-description"
                  className="w-full bg-black/30 border border-white/10 rounded-md p-2.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-primary" required />
              </div>

              {err && <div data-testid="ticket-error" className="text-sm text-[#FF453A] bg-[#FF453A]/10 border border-[#FF453A]/30 rounded-md p-3">{err}</div>}

              <Button type="submit" disabled={submitting} data-testid="ticket-submit"
                className="w-full h-11 bg-gradient-to-r from-primary to-[#0056B3] hover:from-[#0056B3] hover:to-primary text-white font-semibold group">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Kirim Pengajuan <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} /></>)}
              </Button>
            </form>
          ) : (
            <div className="space-y-4" data-testid="track-panel">
              <form onSubmit={track} className="flex gap-2">
                <Input value={trackNo} onChange={(e) => setTrackNo(e.target.value)} placeholder="Mis. LYN-00001" data-testid="track-input"
                  className="bg-black/30 border-white/10 text-white uppercase placeholder:normal-case" />
                <Button type="submit" disabled={tracking} data-testid="track-button" className="bg-primary hover:bg-[#0056B3] shrink-0">
                  {tracking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" strokeWidth={1.5} />}
                </Button>
              </form>

              {trackErr && <div data-testid="track-error" className="text-sm text-[#FF453A] bg-[#FF453A]/10 border border-[#FF453A]/30 rounded-md p-3">{trackErr}</div>}

              {trackData && (
                <div className="rounded-xl bg-black/40 border border-white/10 p-5 space-y-3" data-testid="track-result">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-lg font-bold text-primary">{trackData.ticket_no}</span>
                    <span className={`px-2.5 py-1 rounded-full text-xs border ${STATUS_COLOR[trackData.status] || STATUS_COLOR.Baru}`} data-testid="track-status">
                      {trackData.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><div className="text-[10px] uppercase tracking-widest text-white/40">Jenis</div><div className="text-white capitalize">{trackData.type}</div></div>
                    <div><div className="text-[10px] uppercase tracking-widest text-white/40">Topik</div><div className="text-white">{trackData.topic}</div></div>
                    <div><div className="text-[10px] uppercase tracking-widest text-white/40">Pemohon</div><div className="text-white">{trackData.nama}</div></div>
                    <div><div className="text-[10px] uppercase tracking-widest text-white/40">Diajukan</div><div className="text-white">{(trackData.created_at || "").slice(0, 10)}</div></div>
                  </div>
                  {trackData.response_note && (
                    <div className="pt-2 border-t border-white/10">
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Tanggapan</div>
                      <div className="text-sm text-white/80">{trackData.response_note}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="text-center text-white/30 text-[11px] font-mono tracking-wider mt-6">© UT Mataram 2026</div>
      </div>
    </div>
  );
}
